import React, { useEffect, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  Box,
  Container,
  ThemeProvider,
  Typography,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { usePostHog } from 'posthog-js/react';
import * as Sentry from '@sentry/react';

import {
  ContentContainer,
  getChoiceButtonStyle,
} from './AccessCodeSpark.styles';

import sparkTheme from '@/themes/sparkTheme';
import { SelectButton } from '@/components/Button';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { Product } from '@/utils/productHelpers';
import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { usePostHogDistinctId } from '@/hooks/usePostHogDistinctId';

const AccessCodeSparkInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();
  const history = useHistory();
  const theme = useTheme();
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { distinctId: phDistinctId } = usePostHogDistinctId();
  const { product } = useParams<{ product: Product }>();
  const choiceButtonStyle = getChoiceButtonStyle(theme);
  const location = useLocation<{ error?: string }>();
  const { search } = location;
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const accessCode = params.get('accessCode') ?? undefined;

  useEffect(() => {
    Sentry.captureMessage(
      // eslint-disable-next-line max-len
      `[PostHog Debug][AccessCodeSpark] phDistinctId received | ${JSON.stringify({ phDistinctId: phDistinctId ?? null })}`,
      { level: 'info' },
    );
  }, [phDistinctId]);

  useEffect(() => {
    posthog?.capture('SparkAccessCodeScreenViewed');
    posthog?.capture('SignUpFlowLanded');
  }, [posthog, product]);

  useEffect(() => {
    const redirectTo = params.get('redirectTo');
    if (redirectTo && isSafeRedirectUri(redirectTo)) {
      sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectTo);
    }

    if (accessCode) {
      history.push(`/activate/${product}/access-code/entry`, { accessCode });
    }
  }, [params, history, product, accessCode]);

  const handleYesClick = () => {
    posthog?.register({ flow: 'employer' });
    posthog?.capture('SignUpFlowClicked');
    history.push(`/activate/${product}/access-code/entry`, { accessCode });
  };

  const handleNoClick = () => {
    posthog?.register({ flow: 'order' });
    posthog?.capture('SignUpFlowClicked');
    history.push(`/activate/${product}/employers`);
  };

  return (
    <ContentContainer>
      <Header />

      <Container maxWidth="xs" style={{ flex: 1 }}>
        {location.state?.error === 'email_verification_failed' && (
          <Typography color="error" style={{ marginTop: 16 }} data-testid="email-verification-error">
            {t('coverage.pre_onboarding.access_code.email_verification_error')}
          </Typography>
        )}
        <Box style={{ marginTop: 32, marginBottom: 40 }}>
          <Typography variant="h2" style={{ marginBottom: 8 }} color="textPrimary">
            {t('coverage.pre_onboarding.access_code.title')}
          </Typography>
          <Typography color="textPrimary">
            <Trans
              i18nKey="coverage.pre_onboarding.access_code.sub_title"
              components={{ bold: <strong /> }}
            />
          </Typography>
        </Box>

        <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <SelectButton
            aria-pressed={false}
            style={choiceButtonStyle}
            onClick={handleYesClick}
            data-testid="access-code-yes-button"
          >
            {t('coverage.pre_onboarding.access_code.yes')}
          </SelectButton>

          <SelectButton
            aria-pressed={false}
            style={choiceButtonStyle}
            onClick={handleNoClick}
            data-testid="access-code-no-button"
          >
            {t('coverage.pre_onboarding.access_code.no')}
          </SelectButton>
        </Box>
        <ConsentWidget />
      </Container>
      <Footer />
    </ContentContainer>
  );
};

const AccessCodeSpark: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <AccessCodeSparkInner />
  </ThemeProvider>
);

export default AccessCodeSpark;
